import { type LinkComponentProps, Link as RouterLink } from '@tanstack/react-router';
import classNames from 'classnames';
import type { SharedComponentProps } from '../types';
import * as styles from './index.css';

// Re-exporting TanStack Router's Link component for convenience.
// Use this if you just want a plain Link without our custom styles.
// Otherwise, prefer using the Link component defined in this file for consistent styling across the app.
export { RouterLink };

type LinkOwnProps = { noUnderline?: boolean; variants?: keyof typeof styles.variants } & SharedComponentProps<'a'>;

type RouterLinkProps = { href?: undefined } & LinkComponentProps;
type AnchorLinkProps = { href: string } & SharedComponentProps<'a'>;

export type LinkProps = LinkOwnProps & (AnchorLinkProps | RouterLinkProps);

/**
 * Link component that combines the navigation capabilities of TanStack Router's `Link` with our custom styles.
 * Use the `href` prop to indicate whether it's a router link or a regular anchor link.
 * If `href` is provided, it renders a standard `<a>` element, otherwise it renders a `RouterLink` element.
 */
export function Link({ className, noUnderline, variants = 'default', children, ...props }: LinkProps) {
    return props.href ? (
        <a
            className={classNames(
                styles.link,
                styles.variants[variants],
                { [styles.noUnderline]: noUnderline },
                className
            )}
            {...props}
        >
            {children}
        </a>
    ) : (
        <RouterLink
            className={classNames(
                styles.link,
                styles.variants[variants],
                { [styles.noUnderline]: noUnderline },
                className
            )}
            {...props}
        >
            {children}
        </RouterLink>
    );
}
